export interface GroupFunctionHeaderRow {
    isGroup: true;
    groupFunction: string;
}

export class GroupFunctionRows<T extends { GroupFunction?: string | null }> {
    static readonly noGroupLabel = '(No Group)';

    rows: Array<T | GroupFunctionHeaderRow> = [];
    visibleItems: T[] = [];
    itemCount = 0;

    private items: T[] = [];
    private groupNames: string[] = [];
    private readonly expandedGroups = new Set<string>();

    constructor(private readonly onVisibleItemsChange: (visibleItems: T[]) => void = () => { }) { }

    setItems(items: T[]): void {
        const groups = new Map<string, T[]>();
        for (const item of items) {
            const groupName = this.groupOf(item);
            const groupItems = groups.get(groupName);
            if (groupItems)
                groupItems.push(item);
            else
                groups.set(groupName, [item]);
        }

        const sortedGroups = [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
        const rows: Array<T | GroupFunctionHeaderRow> = [];
        sortedGroups.forEach(([groupFunction, groupItems]) => {
            rows.push({ isGroup: true, groupFunction }, ...groupItems);
        });

        this.rows = rows;
        this.items = items;
        this.groupNames = sortedGroups.map(([groupFunction]) => groupFunction);
        this.itemCount = items.length;
        this.refreshVisibleItems();
    }

    isGroupRow = (_index: number, row: T | GroupFunctionHeaderRow): boolean =>
        (row as GroupFunctionHeaderRow).isGroup === true;

    isExpanded(groupFunction: string): boolean {
        return this.expandedGroups.has(groupFunction);
    }

    isItemVisible(item: T): boolean {
        return this.isExpanded(this.groupOf(item));
    }

    toggle(groupFunction: string): void {
        if (this.expandedGroups.has(groupFunction))
            this.expandedGroups.delete(groupFunction);
        else
            this.expandedGroups.add(groupFunction);
        this.refreshVisibleItems();
    }

    get allExpanded(): boolean {
        return this.groupNames.every(groupName => this.isExpanded(groupName));
    }

    toggleAll(): void {
        if (this.allExpanded)
            this.expandedGroups.clear();
        else
            this.groupNames.forEach(groupName => this.expandedGroups.add(groupName));
        this.refreshVisibleItems();
    }

    private refreshVisibleItems(): void {
        this.visibleItems = this.items.filter(item => this.isItemVisible(item));
        this.onVisibleItemsChange(this.visibleItems);
    }

    private groupOf(item: T): string {
        return item.GroupFunction || GroupFunctionRows.noGroupLabel;
    }
}
